import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { Bird, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { EagleControls } from './EagleControls';
import { useEaglePreferences } from './useEaglePreferences';
import { useEagleCanvas } from './useEagleCanvas';
import { getEagleAnimation } from './animations';
import './home-eagle.css';

const EaglePlayer = ({ userId, idPrefix = 'home-eagle', showAppearance = true }) => {
  const { preferences, update, saveError } = useEaglePreferences(userId);
  const animation = getEagleAnimation(preferences.eagleStyle);
  const [allowReducedPlayback, setAllowReducedPlayback] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [failedPoster, setFailedPoster] = useState(null);
  const posterFailed = failedPoster === animation.poster;
  const reducedMotion = useReducedMotion();
  const canvasRef = useRef(null);
  const paused = preferences.paused || (Boolean(reducedMotion) && !allowReducedPlayback);
  const status = useEagleCanvas({ canvasRef, running: !paused, speed: preferences.speed, retryKey, animation });
  useEffect(() => { if (reducedMotion) setAllowReducedPlayback(false); }, [reducedMotion]);
  const toggle = () => {
    if (paused) { setAllowReducedPlayback(true); update({ paused: false }); }
    else update({ paused: true });
  };
  return <section className="ps-home-animation-space home-eagle" data-testid={idPrefix === 'home-eagle' ? 'home-animation-space' : `${idPrefix}-space`}
    aria-label="Eagle mascot animation" data-playback={paused ? 'paused' : 'playing'} data-animation-status={status}
    data-eagle-style={preferences.eagleStyle}
    style={{ backgroundColor: preferences.background }}>
    <canvas ref={canvasRef} className="home-eagle-canvas" data-testid={`${idPrefix}-canvas`} aria-hidden="true" />
    {status !== 'ready' && !posterFailed && <img key={animation.poster} src={animation.poster} alt={animation.description || 'Cartoon Par Seeker eagle holding a golf ball'}
      data-testid={`${idPrefix}-poster`} className="home-eagle-poster" onError={() => setFailedPoster(animation.poster)} />}
    {status === 'error' && <div className="home-eagle-error" data-testid={`${idPrefix}-error`} role="status">
      {posterFailed && <Bird size={30} aria-hidden="true" />}
      <Button type="button" variant="outline" className="h-8 text-xs" data-testid={`${idPrefix}-retry`}
        onClick={() => { setFailedPoster(null); setRetryKey(key => key + 1); }}><RotateCcw size={12} className="mr-1" />Retry animation</Button>
    </div>}
    <EagleControls preferences={preferences} paused={paused} onToggle={toggle} onChange={update} saveError={saveError}
      disabled={status !== 'ready'} idPrefix={idPrefix} showAppearance={showAppearance} />
  </section>;
};

export const HomeEagleAnimation = ({ userId, ...props }) => <EaglePlayer key={userId || 'guest'} userId={userId} {...props} />;